import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { api } from '../api/client';
import type { AuthenticatedUser } from '../api/types';

interface AuthContextValue {
  user: AuthenticatedUser | null;
  /** 첫 마운트 시 token이 있으면 /me로 검증하는 동안 true. */
  initializing: boolean;
  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthenticatedUser | null>(null);
  const [initializing, setInitializing] = useState(false);

  // 새로고침 시 메모리만 쓰는 토큰은 사라지므로 사실상 항상 false.
  // 미래에 localStorage 정책 변경 시 확장 포인트.
  useEffect(() => {
    if (api.getToken()) {
      setInitializing(true);
      api
        .me()
        .then((u) => setUser(u))
        .catch(() => api.setToken(null))
        .finally(() => setInitializing(false));
    }
  }, []);

  const login = useCallback(async (username: string, password: string) => {
    const result = await api.login(username, password);
    api.setToken(result.accessToken);
    setUser(result.user);
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.logout();
    } catch {
      // stateless이라 서버측 폐기가 없어도 무관 — 클라이언트에서 토큰만 비운다.
    }
    api.setToken(null);
    setUser(null);
  }, []);

  const value = useMemo(
    () => ({ user, initializing, login, logout }),
    [user, initializing, login, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
